import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  Upload,
  CheckSquare,
  Truck,
  BarChart3,
  Settings,
  FileBarChart,
  Building2,
  LogOut,
  HelpCircle,
  Shield,
  ShieldAlert,
  Link2,
  Inbox,
  Activity,
  ChevronDown,
  ChevronRight,
  ShoppingBag,
  Mail,
  FileSpreadsheet,
  Settings2,
  Plug,
  Database,
  Brain,
  FileOutput,
  RotateCcw,
  ShieldCheck,
  Bell,
  Home,
  TrendingUp,
  CreditCard,
  UserPlus,
  Rocket,
  Users,
  Pin,
  X,
  GitBranch,
  Globe2,
  Package,
  ScanSearch,
  FileCheck,
  Receipt,
  MessageSquare,
  Ship,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { usePermissionsContext } from '../../lib/permissions';
import { TenantChip } from '../TenantChip';
import { WorkspaceActivityFeed } from '../WorkspaceActivityFeed';
import { navPinsApi } from '../../lib/api';
import { buildSidebarWithGroups, buildSubtreeGroups,
         shouldAutoExpandSubtree } from './sidebarConfig';
import { PinnedSection } from './PinnedSection';

const ICON_MAP = {
  LayoutDashboard, FileText, Upload, CheckSquare, Truck, BarChart3,
  Settings, FileBarChart, Shield, ShieldAlert, Link2, Inbox, Activity, Building2,
  ShoppingBag, Mail, FileSpreadsheet, Settings2, Plug, Database,
  Brain, FileOutput, RotateCcw, ShieldCheck, Bell, GitBranch, Globe2,
  Package, ScanSearch, FileCheck, Receipt, MessageSquare, Ship,
  Home, TrendingUp, CreditCard, UserPlus, Rocket, Users, Pin,
};

// LocalStorage key builder — per-user so group-collapse state stays
// personal even when accounts share a browser (dev-only). Falls back
// to a generic key when the user id is absent.
const COLLAPSE_KEY = (userId) =>
  `meridian.iter236.sidebar_groups.${userId || 'anon'}`;

// Iter261 — subtree open/closed state persisted per workspace.
const SUBTREES_KEY = (workspaceId) =>
  `meridian.iter261.sidebar_subtrees.${workspaceId || 'anon'}`;

// Groups are open by default. We only *persist* deviations from that
// default so first-time users get a fully-expanded sidebar.
function readCollapsedGroups(userId) {
  try {
    const raw = localStorage.getItem(COLLAPSE_KEY(userId));
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function writeCollapsedGroups(userId, set) {
  try {
    localStorage.setItem(
      COLLAPSE_KEY(userId),
      JSON.stringify(Array.from(set)),
    );
  } catch {
    // localStorage disabled — silently no-op (collapse state resets on reload).
  }
}

// Iter261 — Subtrees (EU Returns and any parentHref-based groupings)
// default to OPEN on first visit. We persist the CLOSED set — the
// inverse of the collapse-key convention — because "open on first
// visit" is what the spec asked for.
function readClosedSubtrees(workspaceId) {
  try {
    const raw = localStorage.getItem(SUBTREES_KEY(workspaceId));
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function writeClosedSubtrees(workspaceId, set) {
  try {
    localStorage.setItem(
      SUBTREES_KEY(workspaceId),
      JSON.stringify(Array.from(set)),
    );
  } catch {
    // no-op
  }
}

export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    user, logout, activeWorkspace, isCrossWorkspaceMode,
    isPartnerCrossMode,
  } = useAuth();
  // Iter263 Phase T3/T4 — partner_entitlements (from
  // /api/auth/permissions) drives the Mode A sidebar module gate;
  // `isPartnerCrossMode` drives the Home entry override.
  const { partner_entitlements: partnerEntitlements } = usePermissionsContext() || {};

  const [collapsedGroups, setCollapsedGroups] = useState(
    () => readCollapsedGroups(user?.id),
  );
  const [pinnedIds, setPinnedIds] = useState([]);
  const [activityDrawerOpen, setActivityDrawerOpen] = useState(false);
  useEffect(() => {
    if (!activityDrawerOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !document.querySelector('[role="dialog"][data-state="open"], [role="listbox"], [role="menu"]')) setActivityDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [activityDrawerOpen]);

  // Re-hydrate collapse state whenever the signed-in user changes
  // (e.g. after a login flow or workspace impersonation).
  useEffect(() => {
    setCollapsedGroups(readCollapsedGroups(user?.id));
  }, [user?.id]);

  // Load pins. Backend scopes by (user, workspace) and falls back to
  // the user's tenant_id if no X-Workspace-Id header is present — so
  // we run the GET whenever `user.id` changes and also on workspace
  // switch, but NOT gated on `activeWorkspace?.id` (which is null in
  // sysadmin "Global / All Workspaces" mode).
  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setPinnedIds([]);
      return undefined;
    }
    (async () => {
      try {
        const res = await navPinsApi.get();
        if (cancelled) return;
        setPinnedIds(Array.isArray(res.data?.pin_ids) ? res.data.pin_ids : []);
      } catch {
        if (!cancelled) setPinnedIds([]);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, activeWorkspace?.id]);

  // Iter263 Phase R — partner in workspace mode gets the workflow
  // sidebar; in all-workspaces mode keeps the partner-console sidebar.
  const partnerWorkspaceMode = useMemo(() => {
    if (user?.role !== 'partner_admin' && user?.role !== 'partner_viewer') return false;
    const activeWs = typeof window !== 'undefined'
      ? window.localStorage.getItem('activeWorkspaceId')
      : null;
    const crossMode = typeof window !== 'undefined'
      && window.localStorage.getItem('partnerCrossWorkspaceMode') === 'true';
    return !!activeWs && !crossMode;
  }, [user?.role, activeWorkspace?.id]);

  const filteredNav = useMemo(() => buildSidebarWithGroups(
    user?.role,
    activeWorkspace?.feature_flags,
    { crossWorkspaceMode: !!isCrossWorkspaceMode,
       partnerWorkspaceMode,
       partnerEntitlements,
       isPartnerCrossMode: !!isPartnerCrossMode,
       // Iter S25 Phase 27 — canonical lane list from the workspace
       // doc drives the USA nav gate (see isModuleEnabled).
       enabledLanes: activeWorkspace?.enabled_lanes || null },
  ), [user?.role, activeWorkspace?.feature_flags, isCrossWorkspaceMode,
        partnerWorkspaceMode, partnerEntitlements, isPartnerCrossMode,
        activeWorkspace?.enabled_lanes]);

  // Flat list of visible leaves (no group headers) — used by pinned
  // section resolution + role-scoping the pin toggle affordance.
  const visibleLeaves = useMemo(
    () => filteredNav.filter((it) => it.type !== 'group_header'),
    [filteredNav],
  );

  const toggleGroup = useCallback((groupId) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      writeCollapsedGroups(user?.id, next);
      return next;
    });
  }, [user?.id]);

  const persistPins = useCallback(async (nextIds) => {
    setPinnedIds(nextIds);
    try {
      await navPinsApi.put(nextIds);
    } catch {
      // Keep the optimistic local state — a subsequent successful
      // toggle will re-sync. We don't roll back to avoid UI flicker.
    }
  }, []);

  const togglePin = useCallback((testId) => {
    setPinnedIds((prev) => {
      const isPinned = prev.includes(testId);
      const next = isPinned
        ? prev.filter((id) => id !== testId)
        : [...prev, testId].slice(0, 10);
      // Fire the PUT outside the setter to keep it pure.
      navPinsApi.put(next).catch(() => {});
      return next;
    });
  }, []);

  // Iter256 — collapsible subtrees for items whose child leaves declare
  // `parentHref`. Iter261 — subtrees default to OPEN and their CLOSED
  // state is persisted per workspace so preferences survive navigation.
  const workspaceKey = activeWorkspace?.id || null;
  const [closedSubtrees, setClosedSubtrees] = useState(
    () => readClosedSubtrees(workspaceKey),
  );

  // Re-hydrate whenever the active workspace changes (e.g. sysadmin
  // switching tenants).
  useEffect(() => {
    setClosedSubtrees(readClosedSubtrees(workspaceKey));
  }, [workspaceKey]);

  const toggleSubtree = useCallback((parentHref) => {
    setClosedSubtrees((prev) => {
      const next = new Set(prev);
      if (next.has(parentHref)) next.delete(parentHref);
      else next.add(parentHref);
      writeClosedSubtrees(workspaceKey, next);
      return next;
    });
  }, [workspaceKey]);

  // Backwards-compat name (existing render code reads `expandedSubtrees`
  // as a Set. Invert `closedSubtrees` on read — anything NOT in the
  // closed set is treated as expanded).
  const expandedSubtrees = useMemo(() => {
    return {
      has: (href) => !closedSubtrees.has(href),
    };
  }, [closedSubtrees]);

  const unpin = useCallback((testId) => {
    persistPins(pinnedIds.filter((id) => id !== testId));
  }, [pinnedIds, persistPins]);

  const renderLeaf = (item, opts = {}) => {
    // Sheet-toggle (Recent Activity) — a button instead of a NavLink.
    if (item.type === 'sheet_toggle') {
      const Icon = ICON_MAP[item.iconKey] || Activity;
      return (
        <button
          key={item.testId}
          type="button"
          onClick={() => setActivityDrawerOpen(true)}
          data-testid={item.testId}
          className={cn(
            'sidebar-link w-full text-left',
            activityDrawerOpen && 'sidebar-link-active',
          )}
        >
          <Icon className="w-4 h-4 flex-shrink-0" />
          <span className="sidebar-text">{item.name}</span>
        </button>
      );
    }
    // Iter263 Phase R3-6 — active-state must honour the query string
    // so /partner (Overview) doesn't stay highlighted when the user is
    // on /partner?tab=claims. Uses URL parsing to compare both parts.
    let isActive;
    try {
      const itemUrl = new URL(item.href, 'http://x');
      if (item.href && item.href.includes('?')) {
        isActive = location.pathname === itemUrl.pathname
          && location.search === itemUrl.search;
      } else {
        isActive = (location.pathname === item.href
                     || (item.href !== '/dashboard' && item.href
                         && location.pathname.startsWith(item.href)))
                   && !location.search.includes('tab=');
      }
    } catch (_e) {
      isActive = location.pathname === item.href;
    }
    const Icon = ICON_MAP[item.iconKey] || LayoutDashboard;
    const isPinned = pinnedIds.includes(item.testId);
    // Iter255 — items that declare `parentHref` are visually nested
    // one level under their parent (e.g. EU H7 items under EU Returns).
    const isSubItem = Boolean(item.parentHref);
    // Iter256 — the parent of a subtree renders with an inline chevron
    // toggle button. `opts.hasChildren` / `opts.expanded` / `opts.onToggle`
    // are supplied by the group renderer.
    const hasChildren = Boolean(opts.hasChildren);
    const expanded = Boolean(opts.expanded);
    return (
      <div
        key={item.name}
        className={cn('group/nav relative', isSubItem && 'sidebar-subnav')}
        data-testid={`nav-row-${item.testId}`}
      >
        <NavLink
          to={item.href}
          data-testid={item.testId
            || `nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
          data-active={isActive ? 'true' : 'false'}
          aria-current={isActive ? 'page' : undefined}
          className={cn(
            'sidebar-link',
            (item.isPinnable || hasChildren) && 'pr-8',
            isSubItem && 'sidebar-link-subnav',
            isActive && 'sidebar-link-active',
          )}
        >
          <Icon className="w-4 h-4 flex-shrink-0" />
          <span className="sidebar-text">{item.name}</span>
        </NavLink>
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              opts.onToggle?.();
            }}
            data-testid={`subtree-toggle-${item.testId}`}
            title={expanded ? 'Collapse' : 'Expand'}
            aria-expanded={expanded}
            aria-controls={`subtree-${item.testId}`}
            className={cn(
              'absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded',
              'text-muted-foreground hover:text-foreground hover:bg-background',
              'transition-colors',
            )}
          >
            {expanded
              ? <ChevronDown className="w-3.5 h-3.5" />
              : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
        ) : item.isPinnable ? (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              togglePin(item.testId);
            }}
            data-testid={`pin-toggle-${item.testId}`}
            title={isPinned ? 'Unpin from top' : 'Pin to top'}
            aria-pressed={isPinned}
            className={cn(
              'absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded',
              'transition-opacity',
              isPinned
                ? 'opacity-100 text-warning'
                : 'opacity-0 group-hover/nav:opacity-70 hover:opacity-100 text-muted-foreground',
            )}
          >
            <Pin
              className="w-3.5 h-3.5"
              fill={isPinned ? 'currentColor' : 'none'}
            />
          </button>
        ) : null}
      </div>
    );
  };

  // Iter236 Phase 2 — render the flat array as GROUPS so we can
  // wrap each group's items in a collapsible container. Walk once
  // building `[{ header, items: [] }]`.
  const groupedForRender = [];
  filteredNav.forEach((item) => {
    if (item.type === 'group_header') {
      groupedForRender.push({ header: item, items: [] });
    } else if (groupedForRender.length > 0) {
      groupedForRender[groupedForRender.length - 1].items.push(item);
    }
  });

  return (
    <aside className="sidebar flex flex-col">
      {/* Logo & Workspace Section (fixed top) */}
      <div className="p-4 border-b border-border flex-shrink-0 min-w-0">
        <div className="flex items-center gap-2 mb-3 min-w-0">
          <img
            src="https://customer-assets.emergentagent.com/job_4cf3c2ed-55c0-4e2e-88f8-3504a875a61f/artifacts/0bt8uv0u_faviconM.png"
            alt="Meridian"
            className="h-8 w-8 flex-shrink-0"
          />
          <span className="app-logo truncate" data-testid="sidebar-logo-text">
            MERIDIAN
          </span>
        </div>
        <div className="mt-2 min-w-0" data-testid="sidebar-tenant-chip-wrapper">
          <TenantChip />
        </div>
      </div>

      {/* Navigation (scrollable middle — flex-1 min-h-0 to bound scroll region) */}
      <nav className="flex-1 min-h-0 px-3 py-4 space-y-1 overflow-y-auto">
        {partnerWorkspaceMode && (
          <button
            type="button"
            onClick={() => {
              localStorage.removeItem('activeWorkspaceId');
              localStorage.setItem('partnerCrossWorkspaceMode', 'true');
              navigate('/partner');
            }}
            data-testid="sidebar-back-to-partner"
            className="w-full text-left text-xs uppercase tracking-wide text-primary hover:underline px-2 py-1 mb-2"
          >
            ← Back to Partner Console
          </button>
        )}
        <PinnedSection
          visibleLeaves={visibleLeaves}
          iconMap={ICON_MAP}
          pinnedIds={pinnedIds}
          onUnpin={unpin}
        />

        {groupedForRender.map(({ header, items }) => {
          if (items.length === 0) return null;
          const isCollapsed = collapsedGroups.has(header.groupId);
          const HeaderIcon = ICON_MAP[header.iconKey];
          return (
            <div key={header.testId || header.groupId} className="pt-1">
              <button
                type="button"
                onClick={() => toggleGroup(header.groupId)}
                data-testid={`${header.testId}-toggle`}
                aria-expanded={!isCollapsed}
                className={cn(
                  'w-full px-2 pt-3 pb-1 flex items-center gap-2',
                  'sidebar-section-header tracking-wide',
                  'transition-colors',
                )}
              >
                {HeaderIcon
                  ? <HeaderIcon className="w-3 h-3 flex-shrink-0" />
                  : null}
                <span
                  className="sidebar-text flex-1 text-left"
                  data-testid={header.testId}
                >
                  {header.name}
                </span>
                {isCollapsed
                  ? <ChevronRight className="w-3 h-3 flex-shrink-0" />
                  : <ChevronDown className="w-3 h-3 flex-shrink-0" />}
              </button>
              {!isCollapsed ? (
                <div
                  className="space-y-1"
                  data-testid={`${header.testId}-body`}
                >
                  {(() => {
                    // Iter256 — subtree grouping via pure helpers so
                    // rendering logic is unit-testable in isolation.
                    const parents = buildSubtreeGroups(items);
                    return parents.map((p) => {
                      if (!p.children.length) return renderLeaf(p);
                      const autoOpen = shouldAutoExpandSubtree(p, location.pathname);
                      const expanded = expandedSubtrees.has(p.href) || autoOpen;
                      return (
                        <div key={p.name} data-testid={`subtree-${p.testId}`}>
                          {renderLeaf(p, {
                            hasChildren: true,
                            expanded,
                            onToggle: () => toggleSubtree(p.href),
                          })}
                          {expanded ? (
                            <div
                              id={`subtree-${p.testId}`}
                              className="space-y-1"
                              data-testid={`subtree-body-${p.testId}`}
                            >
                              {p.children.map((c) => renderLeaf(c))}
                            </div>
                          ) : null}
                        </div>
                      );
                    });
                  })()}
                </div>
              ) : null}
            </div>
          );
        })}
      </nav>

      {/* User Section (fixed bottom) */}
      <div className="p-3 border-t border-border flex-shrink-0 min-w-0">
        <div className="flex items-center gap-3 p-2 mb-2">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-semibold text-primary">
              {user?.name?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="min-w-0 sidebar-text">
            <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">
              {user?.role?.replace('_', ' ')}
            </p>
          </div>
        </div>
        <a
          href="mailto:info@meridiandrawback.co.uk"
          className="sidebar-link text-muted-foreground"
        >
          <HelpCircle className="w-4 h-4" />
          <span className="sidebar-text">Help / Support</span>
        </a>
        <button
          onClick={logout}
          data-testid="logout-btn"
          className="sidebar-link text-muted-foreground w-full hover:text-destructive"
        >
          <LogOut className="w-4 h-4" />
          <span className="sidebar-text">Logout</span>
        </button>
      </div>

      {/* Recent Activity right-side drawer — replaces the old fixed
          sidebar footer toggle. Rendered as a Portal at document.body
          so the fixed backdrop isn't inside the sidebar's stacking
          context (backdrop click was being swallowed otherwise). */}
      {activityDrawerOpen ? createPortal(
        <>
          <div
            className="fixed inset-0 z-40 bg-black/20"
            onClick={() => setActivityDrawerOpen(false)}
            data-testid="activity-drawer-backdrop"
            aria-hidden="true"
          />
          <div
            className={cn(
              'fixed right-0 top-0 bottom-0 z-50 w-96 max-w-full',
              'bg-white border-l border-border shadow-xl flex flex-col',
            )}
            data-testid="activity-drawer"
            role="dialog"
            aria-label="Recent Activity"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-semibold">Recent Activity</span>
              </div>
              <button
                type="button"
                onClick={() => setActivityDrawerOpen(false)}
                aria-label="Close recent activity"
                data-testid="activity-drawer-close"
                className="p-1 rounded hover:bg-muted"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-hidden">
              <WorkspaceActivityFeed />
            </div>
          </div>
        </>,
        document.body,
      ) : null}
    </aside>
  );
}
